import {
  PostHogErrorBoundary,
  PostHogProvider as PostHogProviderRoot,
  usePostHog,
} from '@posthog/react'
import React, { useEffect } from 'react'
import { useSelector } from 'react-redux'

import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'
import { selectIsAuth, selectUserData } from '@/reducers/userDataReducer'
import { CURRENT_BUILD_ID, scrubEventProperties, scrubPostHogCaptureResult } from '@/utils'

import { usePostHogLoadedStore } from './post-hog-provider.store'

const isProduction = import.meta.env.MODE === 'production'
const isDevelopment = import.meta.env.MODE === 'development'

export type PostHogProviderProps = {
  children: React.ReactNode
}

export const PostHogProvider = ({ children }: PostHogProviderProps) => {
  return (
    <PostHogProviderRoot
      apiKey={import.meta.env.VITE_PUBLIC_POSTHOG_TOKEN}
      options={{
        api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
        // `undefined` for `isProduction` - to make sure it can be easily controlled by PostHog dashboard
        // For other environments it should be disabled though
        autocapture: isProduction ? undefined : false,
        // Market Shop / Visitor Parking / Package Room pass an auth token via `?token=` in the
        // URL (see getTokenFromURL in @/utils) — scrub it from every captured event
        // (including `$current_url`/`$pathname`) and from session-replay network requests, since
        // neither is scrubbed by default and PostHog captures the URL verbatim otherwise.
        // `properties.token` (the project API key) is deliberately kept, and `$set`/`$set_once` are
        // scrubbed too — see scrubPostHogCaptureResult.
        before_send: scrubPostHogCaptureResult,
        capture_dead_clicks: isProduction ? undefined : false,
        capture_exceptions: isProduction ? undefined : false,
        defaults: '2026-01-30',
        disable_session_recording: !isProduction,
        // `PostHogProviderRoot` calls `posthog.init()` in its own `useEffect`. Since it's
        // the parent of `PostHogIdentify`, that effect fires *after* PostHogIdentify's
        // effects — React fires effects bottom-up on mount. posthog-js's automatic
        // initial `$pageview` capture happens as part of the same init sequence, so any
        // register()/identify() call made from a PostHogIdentify effect can race it and
        // end up missing from that first event. `loaded` is posthog-js's own hook for
        // this exact problem: it's guaranteed to run before any capturing occurs, so
        // static properties belong here rather than in a mount effect. See EL-13398.
        loaded: (ph) => {
          ph.register({ web_app_build_id: CURRENT_BUILD_ID })
          usePostHogLoadedStore.getState().setLoaded()
        },
        opt_out_capturing_by_default: !isProduction,
        session_recording: {
          // Session replay records every fetch/XHR by default, capturing the token-bearing URL
          // (and any auth headers) verbatim in the recording rather than in a capturable event —
          // `before_send` above can't reach this. Same scrubber, same reasoning.
          maskCapturedNetworkRequestFn: (data) => scrubEventProperties(data),
        },
      }}
    >
      <PostHogErrorBoundary>
        <PostHogIdentify>{children}</PostHogIdentify>
      </PostHogErrorBoundary>
    </PostHogProviderRoot>
  )
}

type AnalyticsBuilding = {
  id: number
  name?: string
}

// Residents (`Client`) belong to exactly one building, which the BE returns as `buildingId`
// (populated into the full building object by `GET /client`). They never get a
// `defaultBuildingId` (only multi-building roles do) and the Navbar renders a logo instead of
// `NavbarBuildingSelector` for them, so `activeBuilding` is never set either. Without this
// fallback, web-only residents end up in PostHog with no building at all.
// `buildingId` means something else for other roles, so only read it for `Client`.
type ClientUserData = {
  buildingId?: AnalyticsBuilding | number | null
  user?: { role?: string }
}

const getClientBuilding = (userData: ClientUserData | null): AnalyticsBuilding | null => {
  if (userData?.user?.role !== 'Client' || !userData.buildingId) return null

  const { buildingId } = userData

  return typeof buildingId === 'object'
    ? { id: buildingId.id, name: buildingId.name }
    : { id: buildingId }
}

const PostHogIdentify = ({ children }: { children: React.ReactNode }) => {
  const postHog = usePostHog()
  // Gates the effects below until after posthog-js's `loaded` callback has fired (see the
  // comment on `options.loaded` above) — calling register()/identify() any earlier can
  // race the SDK's own init sequence and silently drop from the first captured event.
  const isPostHogLoaded = usePostHogLoadedStore((state) => state.isLoaded)

  // TODO: migrate off redux
  const isAuth = useSelector(selectIsAuth)
  const userData = useSelector(selectUserData)
  const activeBuilding = useSelector(selectActiveBuilding)
  const clientBuilding = getClientBuilding(userData)
  const analyticsBuilding: AnalyticsBuilding | null =
    activeBuilding?.id && activeBuilding?.name ? activeBuilding : clientBuilding

  // Note: `@posthog/react` loads `posthog-js` as an ES module, so — unlike the classic
  // `<script>` snippet — it never attaches itself to `window.posthog` on its own. Exposing
  // it here (dev only) makes local console debugging/testing possible, e.g. overriding
  // feature flags to test `useForcedReload` — see EL-13298.
  useEffect(() => {
    if (isDevelopment) {
      window.posthog = postHog
    }
  }, [postHog])

  useEffect(() => {
    if (!isPostHogLoaded) return

    if (isAuth && userData?.user?.id) {
      postHog.identify(userData.user.id.toString(), {
        // NOTE: no PII information sent to PostHog
        building_id: userData.defaultBuildingId ?? clientBuilding?.id,
        role: userData.user.role,
        web_app_build_id: CURRENT_BUILD_ID,
      })
      postHog.register({ role: userData.user.role })
    } else if (!isAuth) {
      postHog.reset(true)
      // `reset()` wipes every registered super property, including `web_app_build_id` —
      // re-assert it immediately, otherwise it silently disappears from every event's
      // properties while still showing up as a person property (set explicitly via
      // `identify()` above). See EL-13398.
      postHog.register({ web_app_build_id: CURRENT_BUILD_ID })
    }
  }, [isAuth, userData, clientBuilding?.id, postHog, isPostHogLoaded])

  useEffect(() => {
    if (!isPostHogLoaded) return

    if (!analyticsBuilding?.id) {
      postHog.unregister('building_id')
      postHog.resetGroups()
      return
    }

    const analyticsBuildingStringId = analyticsBuilding.id.toString()

    postHog.register({ building_id: analyticsBuildingStringId })
    postHog.group(
      'building',
      analyticsBuildingStringId,
      analyticsBuilding.name ? { name: analyticsBuilding.name } : undefined,
    )
  }, [analyticsBuilding?.id, analyticsBuilding?.name, postHog, isPostHogLoaded])

  return <>{children}</>
}
